'use client';

import { useState } from 'react';
import { Badge, Button, Loader } from '@/shared/components/ui';
import { formatDateTimeFull, formatFileSize } from '@/shared/formatters';
import {
  certidoesDoEncerramento,
  GrupoEncerramentoCertificados,
} from '@/features/encerramento-processo';
import {
  FeatherAward,
  FeatherCheckCircle,
  FeatherDownload,
  FeatherExternalLink,
  FeatherFileText,
  FeatherInfo,
  FeatherMail,
  FeatherShield,
  FeatherUser,
} from '@subframe/core';
import {
  CATEGORIA_ARBITRAL_TEMPLATE_LABEL,
  type CategoriaArbitralTemplate,
  isCategoriaArbitralTemplate,
} from '@/types/arbitral/categoriaArbitralTemplate';
import {
  type CertificadoGerado,
  TIPO_CERTIFICADO_LABEL,
  type TipoCertificado,
} from '../../../types/arbitralDetailTypes';

interface ArbitralCertificadosTabProps {
  certificados: CertificadoGerado[];
  isLoading?: boolean;
  onVerCertificadoAcessos: () => void;
  /** Só quem já tem sentença no processo passa: sem ela não há intimação a comprovar. */
  onVerCertificadoAcessosSentenca?: () => void;
  onBaixarCertificado: (certificadoId: string) => Promise<void>;
}

function CartaoCertificadoAcessos({
  titulo,
  selo,
  descricao,
  onVer,
}: {
  titulo: string;
  selo: string;
  descricao: string;
  onVer: () => void;
}) {
  return (
    <div className="border-brand-200 bg-brand-50 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-solid px-5 py-4">
      <div className="flex items-center gap-4">
        <div className="border-brand-100 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-solid bg-white">
          <FeatherShield className="text-brand-600 h-4 w-4" />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body text-default-font font-medium">{titulo}</span>
            <Badge variant="brand">{selo}</Badge>
          </div>
          <span className="text-caption text-subtext-color max-w-xl">{descricao}</span>
        </div>
      </div>
      <Button variant="brand-primary" icon={<FeatherExternalLink />} size="small" onClick={onVer}>
        Ver certificado
      </Button>
    </div>
  );
}

// Partial, não Record cheio: Termo de Imparcialidade/Arbitragem são documentos do processo (aba
// Documentos), não comprovantes emitidos automaticamente pela plataforma — de propósito, ficam
// fora deste mapa. A aba Certificados existe só pra Certificado de Acessos e Certificado de
// Sentença (A6/A7); o filtro em `conhecidos` já descarta qualquer tipo sem entrada aqui.
const TIPO_CONFIG: Partial<
  Record<
    TipoCertificado,
    {
      label: string;
      grupo: string;
      icon: React.ReactNode;
      badgeVariant: 'neutral' | 'brand' | 'success' | 'warning';
    }
  >
> = {
  COMPROVANTE_PROTOCOLO: {
    label: TIPO_CERTIFICADO_LABEL.COMPROVANTE_PROTOCOLO,
    grupo: 'Abertura',
    icon: <FeatherFileText className="text-brand-600 h-4 w-4" />,
    badgeVariant: 'brand',
  },
  CERTIFICADO_PAGAMENTO: {
    label: TIPO_CERTIFICADO_LABEL.CERTIFICADO_PAGAMENTO,
    grupo: 'Triagem',
    icon: <FeatherCheckCircle className="text-success-600 h-4 w-4" />,
    badgeVariant: 'success',
  },
  COMPROVANTE_NOTIFICACAO: {
    label: TIPO_CERTIFICADO_LABEL.COMPROVANTE_NOTIFICACAO,
    grupo: 'Citação e acesso',
    icon: <FeatherMail className="h-4 w-4 text-neutral-500" />,
    badgeVariant: 'neutral',
  },
  CERTIFICADO_ACESSO: {
    label: TIPO_CERTIFICADO_LABEL.CERTIFICADO_ACESSO,
    grupo: 'Citação e acesso',
    icon: <FeatherUser className="h-4 w-4 text-neutral-500" />,
    badgeVariant: 'neutral',
  },
  CERTIFICADO_INTIMACAO: {
    label: TIPO_CERTIFICADO_LABEL.CERTIFICADO_INTIMACAO,
    grupo: 'Sentença',
    icon: <FeatherShield className="text-warning-600 h-4 w-4" />,
    badgeVariant: 'warning',
  },
  CERTIFICADO_SENTENCA: {
    label: TIPO_CERTIFICADO_LABEL.CERTIFICADO_SENTENCA,
    grupo: 'Sentença',
    icon: <FeatherAward className="text-success-600 h-4 w-4" />,
    badgeVariant: 'success',
  },
};

/**
 * A nomeação tem grupo PRÓPRIO, e não "Sentença" nem "Citação e acesso": ela é ato do início do
 * procedimento, e ver a certidão dela sob "Sentença" num processo ainda sem sentença conta uma
 * história processual errada — o que motivou este ajuste (relato da Secretaria, 30/08/2026).
 *
 * Vem depois de "Triagem" e antes de "Citação e acesso" porque é essa a ordem do rito: no fluxo
 * padrão o `arbitral.sortear_arbitro` roda antes do `arbitral.citar`.
 */
const GRUPO_NOMEACAO = 'Nomeação do árbitro';

/**
 * Override por CATEGORIA do modelo, consultado antes de `TIPO_CONFIG`.
 *
 * Existe porque `tipo` agrupa documentos de atos diferentes: a certidão de nomeação nasce
 * `CERTIFICADO_INTIMACAO`, e por isso aparecia rotulada "Comprovante de intimação" dentro do
 * grupo "Sentença" — dois erros de uma vez, no documento de um ato que acontece na citação.
 *
 * Só as categorias em que o `tipo` MENTE entram aqui. `CERTIDAO_DECURSO` também nasce
 * `CERTIFICADO_INTIMACAO` e herda o mesmo grupo errado, mas em que etapa ela deve aparecer
 * depende de qual prazo decorreu — decisão de produto que não foi tomada, e chutar aqui só
 * trocaria um rótulo errado por outro. Fica no comportamento atual até alguém decidir.
 */
const CATEGORIA_CONFIG: Partial<
  Record<CategoriaArbitralTemplate, (typeof TIPO_CONFIG)[TipoCertificado]>
> = {
  CERTIDAO_NOMEACAO: {
    label: CATEGORIA_ARBITRAL_TEMPLATE_LABEL.CERTIDAO_NOMEACAO,
    grupo: GRUPO_NOMEACAO,
    icon: <FeatherUser className="h-4 w-4 text-neutral-500" />,
    badgeVariant: 'neutral',
  },
  // Mesma mentira do `tipo`, e agora com etapa conhecida: a certidão da triagem nasce
  // `CERTIFICADO_INTIMACAO` e cairia em "Sentença" num processo que mal saiu da triagem. É o
  // caso que `CERTIDAO_DECURSO` não tem — aqui o ato é um só e a etapa não depende de qual
  // prazo decorreu.
  CERTIDAO_TRIAGEM: {
    label: CATEGORIA_ARBITRAL_TEMPLATE_LABEL.CERTIDAO_TRIAGEM,
    grupo: 'Triagem',
    icon: <FeatherFileText className="h-4 w-4 text-neutral-500" />,
    badgeVariant: 'neutral',
  },
};

function configDoCertificado(
  certificado: CertificadoGerado,
): (typeof TIPO_CONFIG)[TipoCertificado] {
  // `isCategoriaArbitralTemplate` e não `if (categoria)`: o DTO chega por cast cru
  // (`store/certificados/services.ts`) e o back tem categorias que este front não conhece
  // (`SENTENCA`, `DESPACHO_DECISAO`) — truthy não prova que a chave existe no mapa (COR-01).
  const porCategoria = isCategoriaArbitralTemplate(certificado.categoria)
    ? CATEGORIA_CONFIG[certificado.categoria]
    : undefined;
  return porCategoria ?? TIPO_CONFIG[certificado.tipo];
}

const GRUPO_ORDER = ['Abertura', 'Triagem', GRUPO_NOMEACAO, 'Citação e acesso', 'Sentença'];

function CertificadoCard({
  certificado,
  config,
  onBaixar,
}: {
  certificado: CertificadoGerado;
  config: NonNullable<(typeof TIPO_CONFIG)[TipoCertificado]>;
  onBaixar: (certificadoId: string) => Promise<void>;
}) {
  const [downloading, setDownloading] = useState(false);

  const semArquivo = certificado.tamanhoBytes == null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onBaixar(certificado.id);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-solid border-neutral-200 bg-white px-5 py-4 transition-shadow hover:shadow-sm">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-solid border-neutral-100 bg-neutral-50">
          {config.icon}
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body text-default-font font-medium">
              {certificado.nome || config.label}
            </span>
            <Badge variant={config.badgeVariant}>{config.label}</Badge>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="text-caption text-subtext-color">
              Emitido em {formatDateTimeFull(certificado.dataEmissao)}
            </span>
            {certificado.destinatario && (
              <>
                <span className="text-subtext-color opacity-40">·</span>
                <span className="text-caption text-subtext-color">
                  Para: {certificado.destinatario}
                </span>
              </>
            )}
            {certificado.tamanhoBytes != null && (
              <>
                <span className="text-subtext-color opacity-40">·</span>
                <span className="text-caption text-subtext-color">
                  {formatFileSize(certificado.tamanhoBytes)}
                </span>
              </>
            )}
            {certificado.status === 'PENDENTE' && (
              <>
                <span className="text-subtext-color opacity-40">·</span>
                <span className="text-caption text-subtext-color">Em geração...</span>
              </>
            )}
            {certificado.status === 'GERADO' && semArquivo && (
              <>
                <span className="text-subtext-color opacity-40">·</span>
                <span className="text-caption text-subtext-color">Arquivo indisponível</span>
              </>
            )}
          </div>
        </div>
      </div>

      <Button
        variant="neutral-secondary"
        icon={<FeatherDownload />}
        size="small"
        disabled={certificado.status !== 'GERADO' || semArquivo || downloading}
        onClick={handleDownload}
      >
        Baixar
      </Button>
    </div>
  );
}

export function ArbitralCertificadosTab({
  certificados,
  isLoading,
  onVerCertificadoAcessos,
  onVerCertificadoAcessosSentenca,
  onBaixarCertificado,
}: ArbitralCertificadosTabProps) {
  const certidoesEncerramento = certidoesDoEncerramento(certificados);
  // A certidão de trânsito tem `tipo = CERTIFICADO_SENTENCA` (o `tipo` não distingue — ADR do
  // discriminante), então ela casaria TAMBÉM no grupo "Sentença" e apareceria duas vezes na aba
  // agora que o grupo "Encerramento" recebe dado. O grupo próprio é o que a designer desenhou
  // para ela; o agrupamento por tipo cede.
  const idsNoEncerramento = new Set(certidoesEncerramento.map((certidao) => certidao.id));
  const conhecidos = certificados.filter(
    (c) => configDoCertificado(c) !== undefined && !idsNoEncerramento.has(c.id),
  );

  const grupos = GRUPO_ORDER.reduce<Record<string, CertificadoGerado[]>>((acc, grupo) => {
    const items = conhecidos.filter((c) => configDoCertificado(c)?.grupo === grupo);
    if (items.length > 0) acc[grupo] = items;
    return acc;
  }, {});
  const hasGerados = conhecidos.length > 0;

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader size="medium" />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-lg border border-solid border-neutral-200 bg-neutral-50 px-4 py-3">
        <FeatherInfo className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400" />
        <div className="flex flex-col gap-0.5">
          <span className="text-caption text-default-font font-medium">
            Comprovantes do processo
          </span>
          <span className="text-caption text-subtext-color">
            Provas de que cada ato do processo aconteceu (protocolo, pagamento, citação, prazos,
            trânsito em julgado). Têm validade jurídica e são emitidos automaticamente. Guarde-os em
            local seguro.
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Acesso e Participação
        </span>
        <CartaoCertificadoAcessos
          titulo="Certificado de Acessos"
          selo="Sempre disponível"
          descricao="Reúne as provas de citação — envios, entregas, leituras e acessos do requerido ao processo, com data, hora e origem."
          onVer={onVerCertificadoAcessos}
        />
        {onVerCertificadoAcessosSentenca && (
          <CartaoCertificadoAcessos
            titulo="Certificado de Acessos da Sentença"
            selo="Após a sentença"
            descricao="Reúne as provas da intimação da sentença — envios, entregas, leituras e acessos de cada parte, com data, hora e origem."
            onVer={onVerCertificadoAcessosSentenca}
          />
        )}
      </div>

      {/* Grouped lists */}
      {hasGerados ? (
        Object.entries(grupos).map(([grupo, items]) => (
          <div key={grupo} className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="text-body-bold font-body-bold text-default-font">{grupo}</span>
              <Badge variant="neutral">{items.length}</Badge>
            </div>
            <div className="flex flex-col gap-2">
              {items.map((cert) => (
                <CertificadoCard
                  key={cert.id}
                  certificado={cert}
                  config={configDoCertificado(cert)!}
                  onBaixar={onBaixarCertificado}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50 px-4 py-6 text-center">
          <span className="text-caption text-subtext-color">
            Nenhum outro certificado emitido ainda. Os documentos oficiais da câmara aparecem aqui
            conforme o processo avança.
          </span>
        </div>
      )}

      <GrupoEncerramentoCertificados
        carregando={isLoading}
        certidoes={certidoesEncerramento}
        onBaixar={onBaixarCertificado}
      />
    </div>
  );
}
